import { SERIES, statusColor } from "../../Reports/chartTheme";

/* --------------------------------------------------------------------------
   Turns the /projects/reports payload into what the portfolio page shows.
   Pure and dependency-light so every number on the page can be unit tested.
   -------------------------------------------------------------------------- */

const DAY = 24 * 60 * 60 * 1000;

export const HEALTH = {
  offTrack: { key: "offTrack", label: "Off track", tone: "red", rank: 0 },
  atRisk: { key: "atRisk", label: "At risk", tone: "amber", rank: 1 },
  unplanned: { key: "unplanned", label: "Nothing planned", tone: "slate", rank: 2 },
  idle: { key: "idle", label: "No active sprint", tone: "slate", rank: 3 },
  onTrack: { key: "onTrack", label: "On track", tone: "green", rank: 4 },
};

export const HEALTH_HELP =
  "Compares work done with time used in the active sprint. On track: progress is within 15 points of the time elapsed. At risk: 15–35 points behind. Off track: further behind, or past the end date with work left.";

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

export const percent = (done, total) => (total > 0 ? Math.round((done / total) * 100) : 0);

/**
 * Health of a sprint from progress against elapsed time.
 * `sprint` uses the view shape: { total, done, startDate, endDate }.
 */
export const sprintHealth = (sprint, now = new Date()) => {
  if (!sprint) return "idle";
  const total = Number(sprint.total) || 0;
  const done = Number(sprint.done) || 0;
  if (!total) return "unplanned";

  const progress = done / total;
  if (progress >= 1) return "onTrack";

  const start = toDate(sprint.startDate);
  const end = toDate(sprint.endDate);
  if (!start || !end || end <= start) return progress >= 0.5 ? "onTrack" : "atRisk";
  if (now > end) return "offTrack";

  const elapsed = clamp((now - start) / (end - start));
  const gap = elapsed - progress;
  if (gap <= 0.15) return "onTrack";
  if (gap <= 0.35) return "atRisk";
  return "offTrack";
};

export const daysLeftLabel = (days) => {
  if (days === null || days === undefined) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Ends today";
  if (days === 1) return "1 day left";
  return `${days} days left`;
};

/** A single project, with everything the tile, row and detail panel need. */
export const buildProjectView = (report = {}, index = 0, now = new Date()) => {
  const history = (report.sprintsInfo || [])
    .filter(Boolean)
    .map((sprint) => {
      const total = Math.max(0, Number(sprint.totalIssues) || 0);
      const done = Math.min(Math.max(0, Number(sprint.IssuesDone) || 0), total);
      return {
        id: String(sprint._id || sprint.name),
        name: sprint.name || "Sprint",
        startDate: sprint.startDate || null,
        endDate: sprint.endDate || null,
        active: Boolean(sprint.active),
        completed: Boolean(sprint.completed),
        total,
        done,
        remaining: total - done,
        percent: percent(done, total),
        pointsTotal: Number(sprint.storyPointsTotal) || 0,
        pointsDone: Number(sprint.storyPointsDone) || 0,
      };
    })
    // oldest first, so charts read left to right
    .sort((a, b) => (toDate(a.startDate)?.getTime() || 0) - (toDate(b.startDate)?.getTime() || 0));

  const active = history.find((sprint) => sprint.active) || null;
  const lastCompleted = [...history].reverse().find((sprint) => sprint.completed) || null;
  const focus = active || lastCompleted || history[history.length - 1] || null;

  const end = toDate(active?.endDate);
  const daysLeft = active && end ? Math.ceil((end.getTime() - now.getTime()) / DAY) : null;

  const completedSprints = history.filter((sprint) => sprint.completed);
  const velocity = completedSprints.length
    ? Math.round((completedSprints.reduce((total, sprint) => total + sprint.done, 0) / completedSprints.length) * 10) / 10
    : null;
  const lastTwo = completedSprints.slice(-2);
  const trend = lastTwo.length === 2 ? Math.sign(lastTwo[1].percent - lastTwo[0].percent) : 0;

  const team = (report.contributors || []).filter(Boolean).map((member) => ({
    id: String(member.employeeId || member.employeeEmail || member.employeeName || ""),
    name: member.employeeName || member.employeeEmail || "Member",
    email: member.employeeEmail || "",
    role: member.role || "",
  }));

  const summary = report.issueSummary;
  const issues = summary
    ? {
        total: summary.total || 0,
        done: summary.done || 0,
        open: summary.open || 0,
        backlog: summary.backlog ?? null,
        unassigned: summary.unassigned ?? null,
        byStatus: summary.byStatus || {},
        byType: summary.byType || {},
        byPriority: summary.byPriority || {},
        storyPoints: summary.storyPoints || { total: 0, done: 0 },
      }
    : {
        // older API: all we know is the focus sprint
        total: focus?.total || 0,
        done: focus?.done || 0,
        open: focus?.remaining || 0,
        backlog: null,
        unassigned: null,
        byStatus: {},
        byType: {},
        byPriority: {},
        storyPoints: { total: focus?.pointsTotal || 0, done: focus?.pointsDone || 0 },
      };

  const health = active ? sprintHealth(active, now) : "idle";

  return {
    id: String(report.ProjectId || index),
    name: report.projectName || "Untitled project",
    key: report.projectKey || "",
    slug: report.slug || "",
    lead: report.lead || null,
    color: SERIES[index % SERIES.length],
    history,
    active,
    lastCompleted,
    focus,
    completion: focus ? focus.percent : 0,
    health,
    healthMeta: HEALTH[health],
    daysLeft,
    velocity,
    trend,
    team,
    issues,
    sprintSummary: report.activeSprintSummary || null,
    lastActivity: report.lastActivity || null,
  };
};

const mergeCounts = (views, field) =>
  views.reduce((merged, view) => {
    Object.entries(view.issues[field] || {}).forEach(([key, count]) => {
      merged[key] = (merged[key] || 0) + (Number(count) || 0);
    });
    return merged;
  }, {});

/** Roll-up across every selected project. */
export const buildPortfolio = (views = []) => {
  const done = views.reduce((total, view) => total + (view.focus?.done || 0), 0);
  const total = views.reduce((total, view) => total + (view.focus?.total || 0), 0);

  const people = new Set();
  views.forEach((view) =>
    view.team.forEach((member) => people.add(String(member.email || member.id || member.name).toLowerCase()))
  );

  const healthCounts = { onTrack: 0, atRisk: 0, offTrack: 0, idle: 0 };
  views.forEach((view) => {
    if (view.health === "unplanned" || view.health === "idle") healthCounts.idle += 1;
    else healthCounts[view.health] += 1;
  });

  const hasBacklog = views.some((view) => view.issues.backlog !== null);
  const hasUnassigned = views.some((view) => view.issues.unassigned !== null);

  return {
    projects: views.length,
    activeSprints: views.filter((view) => view.active).length,
    completion: percent(done, total),
    done,
    total,
    open: views.reduce((sum, view) => sum + view.issues.open, 0),
    backlog: hasBacklog ? views.reduce((sum, view) => sum + (view.issues.backlog || 0), 0) : null,
    unassigned: hasUnassigned ? views.reduce((sum, view) => sum + (view.issues.unassigned || 0), 0) : null,
    people: people.size,
    healthCounts,
    byStatus: mergeCounts(views, "byStatus"),
    byType: mergeCounts(views, "byType"),
    byPriority: mergeCounts(views, "byPriority"),
    storyPoints: {
      total: views.reduce((sum, view) => sum + (view.issues.storyPoints?.total || 0), 0),
      done: views.reduce((sum, view) => sum + (view.issues.storyPoints?.done || 0), 0),
    },
  };
};

export const SORTS = {
  health: { label: "Needs attention", compare: (a, b) => a.healthMeta.rank - b.healthMeta.rank || a.completion - b.completion },
  progress: { label: "Most progress", compare: (a, b) => b.completion - a.completion || a.name.localeCompare(b.name) },
  name: { label: "Name", compare: (a, b) => a.name.localeCompare(b.name) },
  team: { label: "Team size", compare: (a, b) => b.team.length - a.team.length || a.name.localeCompare(b.name) },
};

export const sortViews = (views = [], key = "health") => [...views].sort((SORTS[key] || SORTS.health).compare);

export const filterViews = (views = [], query = "") => {
  const term = query.trim().toLowerCase();
  if (!term) return views;
  return views.filter((view) => view.name.toLowerCase().includes(term) || view.key.toLowerCase().includes(term));
};

/**
 * Rows for the delivery-trend chart: each project's sprints aligned from the
 * most recent backwards, so "Latest" means the same thing for every project.
 */
export const trendRows = (views = []) => {
  const length = Math.max(0, ...views.map((view) => view.history.length));
  return Array.from({ length }, (_, slot) => {
    const fromEnd = length - 1 - slot;
    const row = { slot: fromEnd === 0 ? "Latest" : `Sprint −${fromEnd}` };
    views.forEach((view) => {
      const sprint = view.history[view.history.length - 1 - fromEnd];
      row[view.id] = sprint ? sprint.percent : null;
      row[`${view.id}__name`] = sprint ? sprint.name : null;
    });
    return row;
  });
};

const TYPE_COLORS = { Story: SERIES[3], Bug: SERIES[7], Task: SERIES[0], Epic: SERIES[6] };
const PRIORITY_COLORS = { Urgent: SERIES[1], High: SERIES[7], Medium: SERIES[0], Low: SERIES[2] };
const PRIORITY_ORDER = ["Urgent", "High", "Medium", "Low"];
const STATUS_ORDER = ["to do", "in progress", "code review", "in review", "in testing", "qa", "production ready", "done"];

/** { name: count } → ordered segments with colours, for donuts and bars. */
export const toSegments = (counts = {}, kind = "status") => {
  const entries = Object.entries(counts || {}).filter(([, value]) => Number(value) > 0);
  const colorFor = (name, index) => {
    if (kind === "type") return TYPE_COLORS[name] || SERIES[(index + 4) % SERIES.length];
    if (kind === "priority") return PRIORITY_COLORS[name] || SERIES[(index + 5) % SERIES.length];
    return statusColor(name);
  };
  const rank = (name) => {
    if (kind === "priority") {
      const position = PRIORITY_ORDER.indexOf(name);
      return position < 0 ? 99 : position;
    }
    if (kind === "status") {
      const position = STATUS_ORDER.indexOf(String(name).toLowerCase());
      return position < 0 ? 50 : position;
    }
    return 0;
  };
  return entries
    .sort(([a, av], [b, bv]) => rank(a) - rank(b) || Number(bv) - Number(av))
    .map(([label, value], index) => ({ label, value: Number(value), color: colorFor(label, index) }));
};
